import React from "react";
import {
  Award,
  Calendar,
  Heart,
  Megaphone,
  Shield,
  Sprout,
  Trophy,
  Users,
  Vote,
} from "lucide-react";
import useRevealOnScroll from "../hooks/useRevealOnScroll";

const FOUNDERS = [
  "Mr. Raphael Donald",
  "Mr. Faust Castellino",
  "Dr. Louis D'Silva",
  "Mr. George Menezes",
  "Dr. Allan Saldanha",
];

const MILESTONES = [
  {
    year: "1989",
    title: "Foundation",
    text: "Founded on 20 January under the patronage of Archbishop Cardinal Simon Pimenta. Registered as a Public Trust under the Mumbai Public Trusts Act, 1950. Regn. No. 12545 (BOM)",
  },
  {
    year: "1990s–2000s",
    title: "Building the movement",
    text: "Past Secretary Mr. Lavy D'Costa promoted new parish units and community activity. Under Prof. Lila's leadership, the Annual Academic Awards began—a prestigious tradition that continues every year.",
  },
  {
    year: "2005",
    title: "Sabha Day & renewed focus",
    text: "Under President Mr. Dolphy D'Souza, the Sabha broadened civic, political, social action and youth work. Sabha Day was conceptualised as an annual fundraiser—the first held at St. Joseph Wadala West Grounds, with the Sabha Anthem composed by Marcia D'Cunha.",
  },
  {
    year: "2005–2009",
    title: "Public rallies",
    text: "Large rallies at Azad Maidan and, in 2009, Shivaji Park—where over 90,000 people of all faiths gathered with like-minded groups to protest injustice against the community.",
  },
  {
    year: "2014+",
    title: "Honouring our guides",
    text: "During Mr. Gordon D'Souza's presidency, unit awards moved to a deanery-wise model. Memorial awards were established in honour of spiritual advisors Late Fr. Salvador Rodrigues and Late Fr. Edward D'Souza.",
  },
  {
    year: "2015+",
    title: "Civil services & civic duty",
    text: "Civil services promotion was scaled up under Mr. Gordon D'Souza and advanced by President Mrs. Rita D'Sa. The Sabha intensified voter registration, list verification, and pre-election Meet Your Candidate programmes.",
  },
];

const HIGHLIGHTS = [
  {
    icon: Calendar,
    title: "Sabha Day",
    text: "Held every year since 2005—our flagship gathering and fundraiser, born from the need to sustain growing parish and city-wide operations.",
  },
  {
    icon: Award,
    title: "Academic Awards",
    text: "An annual celebration of student achievement, rooted in the legacy of Prof. Lila and the unit-building era of the late 1990s.",
  },
  {
    icon: Vote,
    title: "Election awareness",
    text: "National, state and local elections taken seriously—45+ awareness camps across parishes last year, plus Meet Your Candidate events before each poll.",
  },
  {
    icon: Trophy,
    title: "BCS Footcom",
    text: "Youth football since 2004—the first league tournament at Don Bosco Matunga, with finals at St. Xavier's. The Youth Forum carries this legacy forward.",
  },
  {
    icon: Sprout,
    title: "Environment",
    text: "Seminars on waste management, plastic collection and tree plantation. Post-COVID, a drive to plant over one lakh trees across Mumbai and surrounding areas.",
  },
  {
    icon: Megaphone,
    title: "Advocacy",
    text: "Units and members are encouraged to engage on injustice, civic issues and political participation—faith lived in public life.",
  },
];

function ProgrammeCard({ icon: Icon, title, text, delay }) {
  const [ref, visible] = useRevealOnScroll();

  return (
    <li
      ref={ref}
      className={`reveal-on-scroll ${visible ? "is-visible" : ""} group h-full`}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      <div className="h-full bg-ivory-50 border border-line rounded-2xl p-6 transition-all duration-500 ease-out hover:border-burgundy-600/20 hover:shadow-card-hover hover:-translate-y-1.5">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-line text-burgundy-600 mb-4 transition-all duration-500 group-hover:scale-110 group-hover:bg-burgundy-50 group-hover:border-burgundy-600/25 group-hover:shadow-sm">
          <Icon size={18} strokeWidth={2} aria-hidden />
        </span>
        <h4 className="font-heading font-semibold text-ink-900 transition-colors duration-300 group-hover:text-burgundy-600">
          {title}
        </h4>
        <p className="mt-2 text-sm text-ink-700 leading-relaxed">{text}</p>
      </div>
    </li>
  );
}

export default function AboutSection() {
  return (
    <section
      id="about"
      className="py-20 sm:py-28 bg-white border-t border-line"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-4">
            Our story
          </p>
          <h2 className="font-heading font-semibold text-ink-900 text-3xl sm:text-4xl lg:text-[2.75rem] leading-tight tracking-tight">
            Empowering change
          </h2>
          <p className="mt-3 font-heading text-xl sm:text-2xl text-burgundy-600">
            37+ years of service and empowerment
          </p>
          <div className="gold-rule mx-auto my-8" aria-hidden="true" />
          <p className="text-ink-700 leading-relaxed text-base sm:text-lg">
            The Bombay Catholic Sabha is a vital part of the Catholic community in
            Mumbai—building faith and civic responsibility through parish units,
            advocacy and social programmes that empower the laity and promote
            Christian values in public life.
          </p>
        </div>

        {/* Vision */}
        <blockquote className="mt-14 sm:mt-16 max-w-4xl mx-auto border-l-4 border-burgundy-600 bg-burgundy-50/60 rounded-r-2xl px-6 sm:px-10 py-8 sm:py-10">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-600 mb-3">
            Our vision
          </p>
          <p className="font-heading text-xl sm:text-2xl text-ink-900 leading-snug italic">
            &ldquo;Create a Just Society through Involvement in Socio, Civic and
            Political Affairs&rdquo;
          </p>
        </blockquote>

        {/* Founding & facts */}
        <div className="mt-16 sm:mt-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <h3 className="font-heading font-semibold text-2xl text-ink-900">
              Founded in Mumbai, rooted in the Archdiocese
            </h3>
            <p className="mt-4 text-ink-700 leading-relaxed">
              The Bombay Catholic Sabha was founded on{" "}
              <span className="font-medium text-ink-900">20 January 1989</span>{" "}
              under the patronage of Archbishop Cardinal Simon Pimenta. We salute
              our founder members and all who worked tirelessly to establish the
              organisation.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Membership today stands at{" "}
              <span className="font-medium text-ink-900">67,689 and growing</span>.
              The Sabha is the largest lay organisation in the Archdiocese of
              Bombay—and in India—affiliated to the{" "}
              <span className="font-medium text-ink-900">
                All India Catholic Union
              </span>
              . Our patron is the Archbishop of Bombay,{" "}
              <span className="font-medium text-ink-900">
                Archbishop John Rodrigues
              </span>
              .
            </p>
          </div>

          <div className="bg-ivory-50 border border-line rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-burgundy-100 text-burgundy-600">
                <Users size={18} aria-hidden />
              </span>
              <h4 className="font-heading font-semibold text-lg text-ink-900">
                Founder members
              </h4>
            </div>
            <ul className="space-y-2">
              {FOUNDERS.map((name) => (
                <li
                  key={name}
                  className="text-sm text-ink-700 pl-4 border-l-2 border-gold-300/80"
                >
                  {name}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-ink-500 italic">
              …and several others who helped build the Sabha from the ground up.
            </p>
            <div className="mt-6 pt-6 border-t border-line flex items-start gap-3">
              <Shield size={18} className="text-gold-600 shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm text-ink-700 leading-relaxed">
                Registered as a{" "}
                <span className="font-medium text-ink-900">Public Trust</span> under
                The Mumbai Public Trusts Act, 1950. Regn. No. 12545 (BOM)
              </p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-20 sm:mt-24">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-4">
              Milestones
            </p>
            <h3 className="font-heading font-semibold text-2xl sm:text-3xl text-ink-900 leading-tight">
              A journey of parish units, public witness and growing impact
            </h3>
          </div>

          <ol className="mt-10 sm:mt-12 relative space-y-0">
            {MILESTONES.map(({ year, title, text }, index) => (
              <li
                key={year + title}
                className="relative pl-8 sm:pl-10 pb-10 last:pb-0 border-l-2 border-burgundy-100 last:border-transparent"
              >
                <span
                  className="absolute left-0 top-1 -translate-x-1/2 w-3 h-3 rounded-full bg-burgundy-600 ring-4 ring-white"
                  aria-hidden
                />
                <p className="text-xs font-semibold tracking-wider uppercase text-gold-600">
                  {year}
                </p>
                <h4 className="mt-1 font-heading font-semibold text-lg text-ink-900">
                  {title}
                </h4>
                <p className="mt-2 text-sm sm:text-base text-ink-700 leading-relaxed max-w-3xl">
                  {text}
                </p>
                {index < MILESTONES.length - 1 ? (
                  <span className="sr-only">Next milestone</span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>

        {/* Program highlights */}
        <div className="mt-20 sm:mt-24 pt-16 sm:pt-20 border-t border-line">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-4">
              Today&apos;s work
            </p>
            <h3 className="font-heading font-semibold text-2xl sm:text-3xl text-ink-900 leading-tight">
              Programmes that carry our history forward
            </h3>
          </div>

          <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {HIGHLIGHTS.map(({ icon, title, text }, index) => (
              <ProgrammeCard
                key={title}
                icon={icon}
                title={title}
                text={text}
                delay={index * 90}
              />
            ))}
          </ul>
        </div>

        {/* Gratitude & call to action */}
        <div className="mt-16 sm:mt-20 rounded-2xl bg-burgundy-700 text-white overflow-hidden">
          <div className="px-6 sm:px-10 py-10 sm:py-12 grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 text-gold-300 mb-4">
                <Heart size={18} aria-hidden />
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase">
                  With gratitude
                </span>
              </div>
              <p className="text-white/90 leading-relaxed">
                We thank our supporters, sponsors, unit members, collaborating NGOs,
                and especially our office staff for their wonderful support—always
                with a smile. Special thanks to the Archdiocese bishops and parish
                priests for standing behind our initiatives.
              </p>
              <p className="mt-4 text-white/90 leading-relaxed">
                The Sabha encourages every unit and member to engage with injustice,
                elections, and civic and political life—because faith and responsibility
                belong together in the city we serve.
              </p>
            </div>
            <div className="lg:col-span-2 bg-burgundy-800/80 rounded-xl p-6 sm:p-8 border border-burgundy-600/50">
              <p className="font-heading text-lg sm:text-xl text-gold-100 leading-snug">
                Come be a member of the Sabha
              </p>
              <p className="mt-3 text-sm text-white/85 leading-relaxed">
                Feel proud. Feel committed. Feel proudly Indian.
              </p>
              <p className="mt-6 pt-6 border-t border-burgundy-600/60 font-heading italic text-gold-200/95 text-sm sm:text-base">
                Committed to create a better tomorrow, today!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
